/**
 * Coerce LLM/tool JSON artifact payloads into columns + rows for display.
 * Kept separate from JsonArtifactBody so Fast Refresh works (component-only exports).
 */

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function cellText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value
      .map((v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : JSON.stringify(v)))
      .filter(Boolean)
      .join('; ');
  }
  return JSON.stringify(value);
}

const SECTION_ALIASES: Array<{ keys: string[]; label: string }> = [
  { keys: ['strengths', 'strength', 's'], label: 'Strengths' },
  { keys: ['weaknesses', 'weakness', 'w'], label: 'Weaknesses' },
  { keys: ['opportunities', 'opportunity', 'o'], label: 'Opportunities' },
  { keys: ['threats', 'threat', 't'], label: 'Threats' },
];

/** Flatten SWOT section maps into a table (matches backend coerceSectionMap). */
function coerceSectionMap(data: Record<string, unknown>): {
  columns: string[];
  rows: unknown[][];
} | null {
  if (Array.isArray(data['columns']) || Array.isArray(data['rows'])) return null;
  const keyMap = new Map<string, unknown>();
  for (const [rawKey, value] of Object.entries(data)) {
    if (rawKey === '_decision' || rawKey === 'type' || rawKey === 'status') continue;
    keyMap.set(rawKey.toLowerCase().replace(/[^a-z0-9]+/g, ''), value);
  }
  const matched: Array<{ label: string; text: string }> = [];
  for (const section of SECTION_ALIASES) {
    for (const alias of section.keys) {
      const compact = alias.replace(/[^a-z0-9]+/g, '');
      if (!keyMap.has(compact)) continue;
      const text = cellText(keyMap.get(compact));
      if (text.trim()) matched.push({ label: section.label, text });
      break;
    }
  }
  if (matched.length < 2) return null;
  return {
    columns: ['Section', 'Key points', 'Status', 'Decision notes'],
    rows: matched.map((m) => [m.label, m.text, 'PROVISIONAL', '']),
  };
}

/** Strip ```json ... ``` fences models sometimes wrap around table payloads. */
function stripMarkdownFence(raw: string): string {
  const trimmed = raw.trim();
  const fenced = trimmed.match(/^```(?:json|JSON|javascript)?\s*\r?\n?([\s\S]*?)\r?\n?```\s*$/);
  const inner = fenced?.[1];
  return inner != null ? inner.trim() : trimmed;
}

/** Unwrap JSON strings (models often double-encode after body-as-string schema). */
function unwrapJson(value: unknown, depth = 0): unknown {
  if (depth > 4) return value;
  if (typeof value !== 'string') return value;
  const trimmed = stripMarkdownFence(value);
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return unwrapJson(JSON.parse(trimmed) as unknown, depth + 1);
  } catch {
    return value;
  }
}

function columnLabel(col: unknown, index: number): string {
  if (typeof col === 'string' && col.trim()) return col.trim();
  if (isPlainObject(col)) {
    for (const key of ['label', 'name', 'key', 'title', 'header', 'id']) {
      const v = col[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return `Col ${index + 1}`;
}

/** Prefer object-column `key` when mapping object rows. */
function columnKey(col: unknown, index: number): string {
  if (typeof col === 'string' && col.trim()) return col.trim();
  if (isPlainObject(col)) {
    for (const key of ['key', 'name', 'id', 'label', 'title', 'header']) {
      const v = col[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }
  return `Col ${index + 1}`;
}

function tableFromObjectRows(
  rows: Record<string, unknown>[],
  preferredColumns?: unknown[]
): {
  columns: string[];
  rows: unknown[][];
} {
  if (preferredColumns && preferredColumns.length > 0) {
    const labels = preferredColumns.map(columnLabel);
    const keys = preferredColumns.map(columnKey);
    return {
      columns: labels,
      rows: rows.map((row) => keys.map((key) => row[key] ?? '')),
    };
  }
  const columnSet = new Set<string>();
  for (const row of rows) {
    Object.keys(row).forEach((k) => {
      if (k !== '_decision') columnSet.add(k);
    });
  }
  const columns = [...columnSet];
  return {
    columns,
    rows: rows.map((row) => columns.map((col) => row[col] ?? '')),
  };
}

function tableFromScreeningRows(data: Record<string, unknown>): {
  columns: string[];
  rows: unknown[][];
} | null {
  const screening = data['screeningRows'];
  if (!Array.isArray(screening) || screening.length === 0) return null;
  return {
    columns: ['Symbol', 'Status', 'Rationale'],
    rows: screening.map((row) => {
      if (!isPlainObject(row)) return [cellText(row), '', ''];
      return [
        cellText(row['symbol'] ?? row['ticker']),
        cellText(row['status']),
        cellText(row['rationale'] ?? row['notes'] ?? ''),
      ];
    }),
  };
}

function pickRowArray(obj: Record<string, unknown>): unknown[] | null {
  for (const key of ['rows', 'items', 'records', 'results', 'entries', 'values']) {
    const v = obj[key];
    if (Array.isArray(v) && v.length > 0) return v;
  }
  for (const key of ['data', 'table']) {
    const v = obj[key];
    if (Array.isArray(v) && v.length > 0) return v;
  }
  return null;
}

function pickRawColumns(obj: Record<string, unknown>): unknown[] {
  for (const key of ['columns', 'headers', 'cols', 'fields']) {
    const v = obj[key];
    if (Array.isArray(v) && v.length > 0) return v;
  }
  return [];
}

function coerceDirect(unwrapped: unknown): {
  columns: string[];
  rows: unknown[][];
} | null {
  if (Array.isArray(unwrapped) && unwrapped.length > 0) {
    if (unwrapped.every((r) => isPlainObject(r))) {
      return tableFromObjectRows(unwrapped as Record<string, unknown>[]);
    }
    if (unwrapped.every((r) => Array.isArray(r))) {
      const width = Math.max(...unwrapped.map((r) => (r as unknown[]).length), 0);
      return {
        columns: Array.from({ length: width }, (_, i) => `Col ${i + 1}`),
        rows: unwrapped as unknown[][],
      };
    }
  }

  if (!isPlainObject(unwrapped)) return null;

  const sectionMap = coerceSectionMap(unwrapped);
  if (sectionMap) return sectionMap;

  const screening = tableFromScreeningRows(unwrapped);
  const rawColumns = pickRawColumns(unwrapped);
  const rowArray = pickRowArray(unwrapped);

  if (rowArray && rowArray.length > 0) {
    if (rowArray.every((r) => isPlainObject(r))) {
      return tableFromObjectRows(rowArray as Record<string, unknown>[], rawColumns);
    }

    const columns =
      rawColumns.length > 0
        ? rawColumns.map(columnLabel)
        : (rowArray[0] as unknown[]).map((_, i) => `Col ${i + 1}`);

    const rows: unknown[][] = rowArray.map((row) => {
      if (Array.isArray(row)) return row;
      if (isPlainObject(row) && rawColumns.length > 0) {
        return rawColumns.map(columnKey).map((key) => row[key] ?? '');
      }
      return [row];
    });
    return { columns, rows };
  }

  if (screening) return screening;

  return null;
}

/** Flatten plain objects into a two-column Field | Value table. */
function keyValueTable(data: Record<string, unknown>): {
  columns: string[];
  rows: unknown[][];
} | null {
  const skip = new Set(['type', 'kind', '_decision', 'status', 'columns', 'rows', 'blocks']);
  const rows: unknown[][] = [];
  for (const [key, value] of Object.entries(data)) {
    if (skip.has(key) || value == null) continue;
    if (typeof value === 'object' && !Array.isArray(value)) {
      // Nested object: one row with pretty text (prefer nested dig elsewhere first)
      rows.push([humanizeKey(key), cellText(value)]);
      continue;
    }
    const text = cellText(value);
    if (!text.trim()) continue;
    rows.push([humanizeKey(key), text]);
  }
  if (rows.length < 1) return null;
  return { columns: ['Field', 'Value'], rows };
}

function humanizeKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}

const NEST_KEYS = [
  'data',
  'table',
  'result',
  'results',
  'output',
  'content',
  'payload',
  'analysis',
  'body',
  'screening',
  'report',
  'document',
] as const;

/**
 * Best-effort coerce any LLM/tool JSON payload into columns + rows for display.
 */
export function coerceArtifactTable(data: unknown): {
  columns: string[];
  rows: unknown[][];
} | null {
  const seen = new Set<unknown>();

  function dig(value: unknown, depth: number): {
    columns: string[];
    rows: unknown[][];
  } | null {
    if (depth > 6) return null;
    const unwrapped = unwrapJson(value);
    if (unwrapped == null || (typeof unwrapped === 'object' && seen.has(unwrapped))) {
      return null;
    }
    if (typeof unwrapped === 'object') seen.add(unwrapped);

    if (
      isPlainObject(unwrapped) &&
      (unwrapped['type'] === 'json' ||
        unwrapped['type'] === 'table' ||
        unwrapped['type'] === 'data') &&
      unwrapped['data'] !== undefined
    ) {
      const nested = dig(unwrapped['data'], depth + 1);
      if (nested) return nested;
    }

    const direct = coerceDirect(unwrapped);
    if (direct && direct.rows.length > 0) return direct;

    if (isPlainObject(unwrapped)) {
      for (const key of NEST_KEYS) {
        if (unwrapped[key] === undefined) continue;
        const nested = dig(unwrapped[key], depth + 1);
        if (nested) return nested;
      }
      for (const child of Object.values(unwrapped)) {
        if (child != null && typeof child === 'object') {
          const nested = dig(child, depth + 1);
          if (nested) return nested;
        }
      }
      // Last resort: Field | Value for opaque report objects (avoid raw JSON dump)
      const kv = keyValueTable(unwrapped);
      if (kv && kv.rows.length > 0) return kv;
    }

    return null;
  }

  return dig(data, 0);
}

/** Format a cell for table display (shared with JsonArtifactBody). */
export function artifactCellText(value: unknown): string {
  return cellText(value);
}
